import type { TaskStatus } from '@netcon-work/shared'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'

/**
 * **Die Notiz zum gerade vollzogenen Statuswechsel** (25.08.2026,
 * [E-231](../../../../docs/ENTSCHEIDUNGEN.md#e-231)).
 *
 * Nils: *„ggf. sollten wir einen ‚Erledigt'-Kommentar optional abfragen … oder nur bei Abnahme,
 * also wo ein Abnehmer ist."* Die Antwort war: **beides, aber es sind drei verschiedene
 * Notizen** – und sie hängen alle am selben Feld.
 *
 * | Übergang | schreibt | für wen |
 * |---|---|---|
 * | → Review/Abnahme | der Entwickler | den **Abnehmer** – diese steht später in der Erinnerungsmail |
 * | Review → Erledigt | der Abnehmer | die Historie (Nils, 17.08.2026) |
 * | Review → In Arbeit | der Abnehmer | den Entwickler – *„fehlt noch X"* |
 *
 * ## Der Klick wirkt zuerst
 *
 * Dieselbe Bedienung wie beim Pin aus [Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md):
 * der Status springt sofort, **danach** klappt dieses Feld auf. Ein Dialog **vor** dem Wechsel
 * wäre eine Hürde – und aus einer Ein-Klick-Geste würde eine Formularübung, die man nach drei
 * Tagen umgeht.
 *
 * ## Und es ist nie Pflicht
 *
 * Nils' eigene Entscheidung vom 17.08.2026, die im Server steht: *„eine Pflichtbegründung bei
 * jeder Abnahme würde bei 27 Erledigungen in zwölf Tagen zur Formsache, und Formsachen werden
 * mit «ok» ausgefüllt."* Wegklicken kostet einen Klick und hinterlässt nichts.
 */

/** Zu welchen Übergängen überhaupt gefragt wird – und mit welcher Frage. */
export function notizFrage(
  nach: TaskStatus,
  von: TaskStatus | undefined,
  hatAbnehmer: boolean,
): string | null {
  if (nach === 'review') {
    /**
     * **Ohne Abnehmer wird nicht gefragt.** „Review" ist dann bloß ein Ablaufschritt, auf den
     * niemand wartet – ein Feld dort wäre ein Formular für nichts.
     */
    return hatAbnehmer ? 'Was soll der Abnehmer wissen?' : null
  }
  if (von === 'review' && nach === 'erledigt') return 'Etwas zur Abnahme festhalten?'
  if (von === 'review' && nach === 'in_arbeit') return 'Was fehlt noch?'
  return null
}

export function StatusNotiz({
  aufgabeId,
  frage,
  schliessen,
}: {
  aufgabeId: string
  frage: string
  schliessen: () => void
}) {
  const [text, setText] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const eingabe = useRef<HTMLInputElement>(null)
  const feldId = useId()

  useEffect(() => {
    eingabe.current?.focus()
  }, [])

  const speichern = useMutation({
    mutationFn: (notiz: string) => trpc.tasks.statusNotiz.mutate({ id: aufgabeId, notiz }),
    onSuccess: () => schliessen(),
    onError: (f: unknown) =>
      setFehler(f instanceof Error ? f.message : 'Die Notiz ließ sich nicht speichern.'),
  })

  const absenden = (): void => {
    const sauber = text.trim()
    if (sauber === '') {
      schliessen()
      return
    }
    speichern.mutate(sauber)
  }

  return (
    <div className="mt-2 rounded-lg border border-line2 bg-page p-2.5">
      <label className="block text-[11px] font-semibold text-ink2" htmlFor={feldId}>
        {frage}
      </label>
      <input
        id={feldId}
        ref={eingabe}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') absenden()
          if (e.key === 'Escape') schliessen()
        }}
        maxLength={2000}
        placeholder="Optional – ein Satz genügt"
        className="mt-1 w-full rounded-md border border-line2 bg-surface px-2 py-1 text-[12.5px]"
      />

      {fehler && (
        <p role="alert" className="mt-1 text-[11px] text-warn">
          {fehler}
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" variant="primary" onClick={absenden} disabled={speichern.isPending}>
          Speichern
        </Button>
        <button
          type="button"
          onClick={schliessen}
          className="cursor-pointer text-[11.5px] text-muted underline"
        >
          Ohne Notiz
        </button>
      </div>
    </div>
  )
}
